import { Box, Button, Stack, Typography } from '@mui/material';
import { File, UploadCloudIcon } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    AbdomenChestOutlineIcon,
    BodyOutlineIcon,
    CombinationOutlineIcon,
    FemalePelvisOutlineIcon,
    HeadNeckOutlineIcon,
} from '~/assets/prediction-model-icons';
import { SessionPresentation } from '~/backend/connector-api/connector-schemas';
import { EnvironmentObjectIds } from '~/engine/data/Environment';
import { useDataUpload } from '~/hooks/workflows/backend/tasks/useDataUpload';
import useDataUploadTask from '~/hooks/workflows/connector/tasks/useDataUploadTask';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import InputDataDisplay from '../../../components/InputDataPreview/ImageAndStructureSetDisplay';
import PseudonymizeCheckbox from '../../../components/ModelSelection/PseudonymizeCheckbox';
import UploadedBodyPart from './UploadedBodyPart';

export const DATA_UPLOAD_BODY_PARTS = [
    { icon: File, label: 'Unknown', value: 'unknown' },
    { icon: HeadNeckOutlineIcon, label: 'Head and Neck', value: 'head_neck' },
    {
        icon: AbdomenChestOutlineIcon,
        label: 'Abdomen and Chest',
        value: 'abdomen_chest',
    },
    { icon: FemalePelvisOutlineIcon, label: 'Pelvis', value: 'pelvis' },
    {
        icon: CombinationOutlineIcon,
        label: 'Combination',
        value: 'combination',
    },
    { icon: BodyOutlineIcon, label: 'Whole Body', value: 'whole_body' },
];

export default function DataUpload({
    showPseudonymization = true,
    sessionData,
    environmentObjectIds,
}: {
    showPseudonymization?: boolean;
    sessionData?: SessionPresentation;
    environmentObjectIds?: EnvironmentObjectIds;
}) {
    const backendHook = useDataUpload();
    const connectorHook = useDataUploadTask({
        environmentObjectIds,
        patientId: sessionData?.payload?.patient.id || '',
    });

    const { startUpload, isSubmitting } = sessionData
        ? connectorHook
        : backendHook;
    const [action, setAction] = useState<number>(0);
    const primaryImage = useRecoilValue(primaryImageState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="dataUpload" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="dataUploadDescription" />
                </Typography>
            </Box>
            <InputDataDisplay
                image={primaryImage}
                structureSet={currentStructureSet}
                error={null}
            />

            <UploadedBodyPart
                selectedBodyPart={action}
                setSelectedBodyPart={setAction}
                bodyParts={DATA_UPLOAD_BODY_PARTS}
            />

            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
            >
                <Stack gap={0.5}>
                    {showPseudonymization && <PseudonymizeCheckbox />}
                    <Button
                        variant="outlined"
                        color="primary"
                        endIcon={<UploadCloudIcon />}
                        sx={{ width: 160 }}
                        disabled={isSubmitting}
                        onClick={() =>
                            startUpload(DATA_UPLOAD_BODY_PARTS[action].value)
                        }
                    >
                        <FormattedMessage id="send" />
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
